import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { toast } from "react-hot-toast";
import {
  FileText,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Award,
  Clock,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const SingleMainsTestView = () => {
  const { testId } = useParams();
  const token = JSON.parse(localStorage.getItem("user"))?.token;

  const [test, setTest] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTestData();
  }, [testId, token]);

  const fetchTestData = async () => {
  try {
    const testRes = await axios.get(
      `${API_BASE}/api/user/tests/mains/${testId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setTest(testRes.data.test || testRes.data);

  } catch (err) {
    if (err.response?.status === 403) {
      setTest("LOCKED");
      return;
    }

    console.error("❌ Test data error:", err.message);
    toast.error("Error loading test data");
  }

  // Fetch submission only if accessible
  try {
    const subRes = await axios.get(
      `${API_BASE}/api/user/tests/mains/submissions`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const found = subRes.data.find(
      (s) => s.testId && s.testId._id === testId
    );

    if (found) setSubmission(found);

  } catch {
    setSubmission(null);
  }
};

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file");

    const formData = new FormData();
    formData.append("answerFile", file);
    formData.append("testId", testId);

    setUploading(true);
    try {
      await axios.post(`${API_BASE}/api/user/tests/mains/submit`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Test submitted successfully!");
      setFile(null);
      await fetchTestData();
    } catch (err) {
      console.error("❌ Submission error:", err);
      toast.error(
        `Submission failed: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete your submission? This action cannot be undone."
      )
    )
      return;

    setDeleting(true);
    try {
      await axios.delete(
        `${API_BASE}/api/user/tests/mains/submission/${testId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Submission deleted successfully!");
      setSubmission(null);
      await fetchTestData();
    } catch (err) {
      console.error("❌ Delete error:", err);
      toast.error(
        `Delete failed: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setDeleting(false);
    }
  };

  const parseModelAnswer = (answer) => {
    if (!answer) return { introduction: "", body: "", conclusion: "" };

    const sections = { introduction: "", body: "", conclusion: "" };
    const introPattern = /introduction[:\s]*(.*?)(?=body|conclusion|$)/is;
    const bodyPattern = /body[:\s]*(.*?)(?=conclusion|$)/is;
    const conclusionPattern = /conclusion[:\s]*(.*?)$/is;

    const introMatch = answer.match(introPattern);
    const bodyMatch = answer.match(bodyPattern);
    const conclusionMatch = answer.match(conclusionPattern);

    if (introMatch || bodyMatch || conclusionMatch) {
      sections.introduction = introMatch ? introMatch[1].trim() : "";
      sections.body = bodyMatch ? bodyMatch[1].trim() : "";
      sections.conclusion = conclusionMatch ? conclusionMatch[1].trim() : "";
    } else {
      const paragraphs = answer.split("\n\n").filter((p) => p.trim());
      if (paragraphs.length >= 3) {
        sections.introduction = paragraphs[0];
        sections.body = paragraphs.slice(1, -1).join("\n\n");
        sections.conclusion = paragraphs[paragraphs.length - 1];
      } else {
        sections.body = answer;
      }
    }

    return sections;
  };

  const user = JSON.parse(localStorage.getItem("user"));

const loadRazorpay = (src) =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const handleUpgradeClick = async () => {
  const res = await loadRazorpay(
    "https://checkout.razorpay.com/v1/checkout.js"
  );

  if (!res) {
    toast.error("Razorpay failed to load");
    return;
  }

  try {
    const orderRes = await axios.post(
      `${API_BASE}/api/payments/create-order`,
      {
        amount: 3999,
        type: "testsPremium",
      },
      {
        headers: { Authorization: `Bearer ${user.token}` },
      }
    );

    const { order } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "UPSC Journey",
      description: "Mains Test Series",
      order_id: order.id,

      handler: async function (response) {
        await axios.post(
          `${API_BASE}/api/payments/verify-payment`,
          {
            razorpay_order_id: order.id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            type: "testsPremium",
          },
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );

        const updatedUser = { ...user, hasPaid: true };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        toast.success("Premium Activated!");
        window.location.reload();
      },
    };

    new window.Razorpay(options).open();
  } catch {
    toast.error("Payment failed");
  }
};

  if (test === "LOCKED") {
  return (
    <div className="flex items-center justify-center min-h-[400px] dark:bg-gray-900">
      <div className="text-center">
        <div className="text-4xl mb-4">🔒</div>
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
          Premium Test
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Upgrade to access this test
        </p>

        <button
  onClick={handleUpgradeClick}
  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 
             hover:from-blue-700 hover:to-indigo-700 
             text-white rounded-xl font-semibold shadow-lg 
             hover:shadow-xl transition duration-300"
>
  Upgrade Now
</button>
      </div>
    </div>
  );
}

  if (!test)
    return (
      <div className="flex items-center justify-center min-h-[400px] dark:bg-gray-900">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600 dark:text-gray-300">
            Loading test data...
          </p>
        </div>
      </div>
    );

  if (!test.questions || !Array.isArray(test.questions))
    return (
      <div className="text-center mt-10 p-6 dark:bg-gray-900">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 dark:text-red-400">
          Error: Test questions not found
        </p>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8 dark:bg-gray-900">
      {/* Header */}
      <Card className="border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-800">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <div>
              <CardTitle className="text-2xl text-blue-800 dark:text-blue-300">
                {test.title}
              </CardTitle>
              <div className="flex gap-4 mt-2 text-sm text-gray-600 dark:text-gray-300">
                <span>📅 {new Date(test.date).toDateString()}</span>
                {test.subject && <span>📖 {test.subject}</span>}
                {test.section && <span>📂 {test.section}</span>}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Questions */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            Questions
          </h2>
          <Button
            variant="outline"
            onClick={() => setShowAnswers(!showAnswers)}
            className="flex items-center gap-2 dark:border-gray-600 dark:text-gray-200"
          >
            {showAnswers ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            {showAnswers ? "Hide Model Answers" : "Show Model Answers"}
          </Button>
        </div>

        {test.questions.map((q, idx) => {
          const modelAnswer = parseModelAnswer(q.modelAnswer);
          return (
            <Card
              key={idx}
              className="border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 text-sm font-semibold px-3 py-1 rounded-full">
                    Q{idx + 1}
                  </span>
                  <p className="font-semibold text-gray-800 dark:text-gray-200 leading-relaxed">
                    {q.question}
                  </p>
                </div>

                {showAnswers && q.modelAnswer && (
                  <div className="mt-6 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-6">
                    <h4 className="font-semibold text-green-800 dark:text-green-300 mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Model Answer
                    </h4>

                    <div className="space-y-4">
                      {modelAnswer.introduction && (
                        <div>
                          <h5 className="font-medium text-green-700 dark:text-green-300 mb-2">
                            Introduction:
                          </h5>
                          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed pl-4 border-l-2 border-green-300 dark:border-green-700">
                            {modelAnswer.introduction}
                          </p>
                        </div>
                      )}

                      {modelAnswer.body && (
                        <div>
                          <h5 className="font-medium text-green-700 dark:text-green-300 mb-2">
                            Body:
                          </h5>
                          <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed pl-4 border-l-2 border-green-300 dark:border-green-700 whitespace-pre-line">
                            {modelAnswer.body}
                          </div>
                        </div>
                      )}

                      {modelAnswer.conclusion && (
                        <div>
                          <h5 className="font-medium text-green-700 dark:text-green-300 mb-2">
                            Conclusion:
                          </h5>
                          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed pl-4 border-l-2 border-green-300 dark:border-green-700">
                            {modelAnswer.conclusion}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Submission Section */}
      <Card className="border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Upload className="h-5 w-5" />
            Your Submission
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!submission ? (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Upload your answer sheet
                </p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-500 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-gray-700 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-gray-600"
                />
              </div>

              <Button
                disabled={uploading || !file}
                onClick={handleUpload}
                className="w-full"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Submit Test
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    Submitted Successfully
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  📅 Submitted on:{" "}
                  {new Date(submission.submittedAt).toLocaleString()}
                </p>

                {/* Preview */}
                <div className="mb-4">
                  {submission.fileUrl?.endsWith(".pdf") ? (
                    <object
                      data={submission.fileUrl}
                      type="application/pdf"
                      width="100%"
                      height="600px"
                    >
                      <p className="dark:text-gray-300">
                        Preview not available.{" "}
                        <a
                          href={submission.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 underline"
                        >
                          View File
                        </a>
                      </p>
                    </object>
                  ) : submission.fileUrl?.match(/\.(jpeg|jpg|png)$/i) ? (
                    <img
                      src={submission.fileUrl}
                      alt="Submitted Answer"
                      className="max-w-full max-h-96 rounded border dark:border-gray-600"
                    />
                  ) : (
                    <a
                      href={submission.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800"
                    >
                      View File
                    </a>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete & Re-upload
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Evaluation */}
              {submission?.evaluated ? (
                <div className="bg-green-50 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-5 w-5 text-green-700 dark:text-green-300" />
                    <span className="font-semibold text-green-800 dark:text-green-300 text-lg">
                      Evaluation Result
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
                    🏆 <strong>Marks:</strong> {submission.marks}
                  </p>
                  {submission.comment && (
                    <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
                      💬 <strong>Comment:</strong> {submission.comment}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    📅 Reviewed on:{" "}
                    {new Date(submission.reviewedAt).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <span className="font-semibold text-yellow-800 dark:text-yellow-300 text-lg">
                      Awaiting Evaluation
                    </span>
                  </div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-200">
                    Your submission has been received and will be evaluated soon.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SingleMainsTestView;
